import React from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, FormHelperText, Link, Stack, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { commonMessages } from 'utils/commonMessages';
import { PhoneNumberField } from './PhoneNumberField';
import { ClientContactInformation } from 'modules/service-call/types';
import { isEmail, isPhoneNumber } from 'utils/validation';

interface ContactDetailsStepActiveProps {
  completeStep(clientContactInformation: ClientContactInformation): void
}

interface ContactDetailsStepActiveFormData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  termsAndServiceAccepted: boolean
}

export const ContactDetailsStepActive: React.FC<ContactDetailsStepActiveProps> = ({ completeStep }) => {
  const { formatMessage } = useIntl();
  const { register, handleSubmit, formState: { errors } } = useForm<ContactDetailsStepActiveFormData>({});

  const onSubmit = (formData: ContactDetailsStepActiveFormData) => {
    completeStep(formData);
  };

  return (
    <Stack marginLeft={7} gap={4}>
      <Stack gap={3} >
        <TextField
          {...register('firstName', { required: formatMessage(commonMessages.fieldIsRequired) })}
          required
          autoComplete='firstName'
          variant="outlined"
          fullWidth
          error={Boolean(errors.firstName)}
          helperText={errors.firstName?.message}
          label={formatMessage(commonMessages.firstName)}
        />
        <TextField
          {...register('lastName', { required: formatMessage(commonMessages.fieldIsRequired) })}
          required
          autoComplete='lastName'
          variant="outlined"
          fullWidth
          error={Boolean(errors.lastName)}
          helperText={errors.lastName?.message}
          label={formatMessage(commonMessages.lastName)}
        />
        <TextField
          {...register('email', {
            required: formatMessage(commonMessages.fieldIsRequired),
            validate: (value) => isEmail(value || '') ? true : formatMessage(commonMessages.fieldIsEmail)
          })}
          required
          autoComplete='email'
          variant="outlined"
          fullWidth
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          label={formatMessage(commonMessages.emailAddress)}
        />
        <PhoneNumberField
          {...register('phoneNumber', {
            validate: (value) => isPhoneNumber(value || '') ? true : formatMessage(commonMessages.fieldIsPhoneNumber)
          })}
          required
          autoComplete='phoneNumber'
          variant="outlined"
          fullWidth
          error={Boolean(errors.phoneNumber)}
          helperText={errors.phoneNumber?.message}
          label={formatMessage(commonMessages.phoneNumber)}
        />
        <FormGroup>
          <FormControlLabel
            {...register(
              'termsAndServiceAccepted',
              {
                required: formatMessage({
                  id: 'ContractDetailsStepActive.TermsAndServiceAgrementNotAccepted',
                  defaultMessage: 'The terms an conditions aswell as the privacy policy must be accepted'
                })
              }
            )}
            control={<Checkbox />}
            label={
              <FormattedMessage
                id='ContractDetailsStepActive.TermsAndServiceAgrement'
                defaultMessage='I have read and accept the <TermsAndConditionLink>Terms and Conditions</TermsAndConditionLink> and <PrivacyPolicyLink>Privacy Policy</PrivacyPolicyLink>'
                values={{
                  TermsAndConditionLink: chunks => <Link target='_blank' href='/terms-and-conditions'>{chunks}</Link>,
                  PrivacyPolicyLink: chunks => <Link target='_blank' href='/privacy-policy'>{chunks}</Link>,
                }}
              />
            }
          />
          <FormHelperText error>{errors.termsAndServiceAccepted?.message}</FormHelperText>
        </FormGroup>
      </Stack>
      <Button fullWidth variant='contained' onClick={handleSubmit(onSubmit)}>
        {formatMessage(commonMessages.submit)}
      </Button>
    </Stack>
  );
};